import format from "./formatNumber";
import Decimal from "decimal.js";

const MAX_DIGITS = 10;
const MAX_DECIMALS = 8;
/** U+202F narrow no-break space: a plain " " lets long amounts wrap mid-number in tight containers. */
export const INTEGER_SEPARATOR = " ";

/**
 * Formats a number to a string with a limited number of decimals, and spaces for thousands
 *
 * @param {number} balance - The number to format
 * @param {object} [options] - Additional options
 *     param {boolean} [roundForFiat=false] - Whether to only show 2 decimals for fiat currencies
 *     param {number} [maxDigits=MAX_DIGITS] - The maximum number of digits to display
 *     param {boolean} [canHideNumberOverMaxDigits=false] - If true, numbers over maxDigits or after the first non-digit will be hidden with a > sign
 *     param {boolean} [preventRoundingUp=false] - If true, uses Math.floor instead of rounding to prevent values like 99.99999 from becoming 100
 *     param {boolean} [showSmallDigits=false] - If true, show meaningful digits for small values (<0.01) even when roundForFiat is enabled
 * @returns {string}
 */
export const formatBalance = (
  balance: number | string | Decimal | undefined | null,
  options?: {
    roundForFiat?: boolean;
    maxDigits?: number;
    hideBalance?: boolean;
    keepExponential?: boolean;
    canHideNumberOverMaxDigits?: boolean;
    preventRoundingUp?: boolean;
    showSmallDigits?: boolean;
  },
): string => {
  if (balance === undefined || balance === null) return "0";
  // cast balance to number if needed
  const formattedBalance =
    balance instanceof Decimal ? balance.toNumber() : typeof balance === "string" ? Number(balance) : balance;
  const hideBalance = options?.hideBalance ?? false;
  const roundForFiat = options?.roundForFiat ?? false;
  const maxDigits = options?.maxDigits ?? MAX_DIGITS;
  const keepExponential = options?.keepExponential ?? false;
  const canHideNumberOverMaxDigits = options?.canHideNumberOverMaxDigits ?? false;
  const preventRoundingUp = options?.preventRoundingUp ?? false;
  const showSmallDigits = options?.showSmallDigits ?? false;

  const value = Math.abs(formattedBalance);
  const sign = formattedBalance < 0 ? "-" : "";

  // Avoid scientific notation for very small numbers
  if (value < 0.000001 && value > 0 && !canHideNumberOverMaxDigits) {
    return sign + (keepExponential ? value : value.toFixed(MAX_DECIMALS));
  }
  // Force scientific notation for very large numbers
  if (value > 9999999999) {
    return sign + value.toPrecision(MAX_DECIMALS);
  }

  if (value < 0.09 && canHideNumberOverMaxDigits) {
    const decimalsNumber = value.toString().split("-")[1];
    const formattedValue = value.toFixed(decimalsNumber ? parseInt(decimalsNumber) + 1 : value.toString().length - 1);
    const firstNonZeroDigit = formattedValue.match(/[1-9]/);

    if (firstNonZeroDigit && firstNonZeroDigit.index && formattedValue[firstNonZeroDigit.index + 1] !== "0") {
      // Include all zeros before the first non-zero digit
      const zerosBeforeNonZero = `0.${"0".repeat(firstNonZeroDigit.index - 2)}`;

      return `>${zerosBeforeNonZero}${firstNonZeroDigit[0]}`;
    }
  }

  if (hideBalance) return "****";

  const round = (() => {
    if (roundForFiat) {
      // If showSmallDigits is enabled and value is small, use more decimal places
      if (showSmallDigits && value < 0.01 && value > 0) {
        return Math.min(MAX_DECIMALS, Math.max(3, Math.ceil(-Math.log10(value)) + 1));
      }
      return 2;
    }
    if (value > 10 ** (maxDigits - 1)) return 0;
    return MAX_DECIMALS;
  })();

  const params = {
    decimal: ".",
    integerSeparator: INTEGER_SEPARATOR,
  };

  // If preventRoundingUp is enabled and we're dealing with fiat rounding,
  // use Math.floor to truncate instead of rounding up
  let processedValue = value;
  if (preventRoundingUp && roundForFiat) {
    const multiplier = Math.pow(10, round);
    processedValue = Math.floor(value * multiplier) / multiplier;
  }

  const formattedValue = format({ round, ...params })(processedValue);

  const int = (formattedValue.split(".")[0] || "").replace(/\s/g, "");

  const diff = maxDigits - int.length;

  return sign + format({ round: diff >= 0 ? diff : 0, ...params })(Number(formattedValue.replace(/\s/g, "")));
};

export function unformatNumberAsString(balance: string): Decimal {
  return new Decimal(balance.replace(/\s/g, "") || "0");
}

/**
 * Formats a number with fixed decimals and removes trailing zeros
 *
 * @param {number} value - The number to format
 * @param {number} [decimals=6] - The maximum number of decimal places
 * @returns {string} The formatted number without trailing zeros
 *
 * @example
 * formatNumberWithoutTrailingZeros(1.2) // "1.2"
 * formatNumberWithoutTrailingZeros(1.200000) // "1.2"
 * formatNumberWithoutTrailingZeros(1.000000) // "1"
 * formatNumberWithoutTrailingZeros(1.123450, 6) // "1.12345"
 */
export function formatNumberWithoutTrailingZeros(value: number | Decimal, decimals = 6): string {
  return value.toFixed(decimals).replace(/\.?0+$/, "");
}
